import React, { useState, useMemo } from "react";
import { RefreshCw, X, Upload, ScanLine } from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../shared/Modal";
import { FormField } from "../shared/FormField";
import * as api from "../../api";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * AdjustmentPayModal — client flow: pay the difference the admin requested
 * on an order in "Ajuste Pendiente" state. Uploads a receipt image + amount
 * + reference; the backend flips the order to "Ajuste Enviado" for admin
 * verification.
 * --------------------------------------------------------------------------- */
export function AdjustmentPayModal() {
  const ctx = useAppContext();
  const {
    adjustPayOrder, setAdjustPayOrder, paymentMethods, showToast,
    loadOrders, formatBs, user,
  } = ctx;

  const [selectedPmId, setSelectedPmId] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [useCredit, setUseCredit] = useState(false);
  const [busy, setBusy] = useState(false);

  const differenceBs = Number(adjustPayOrder?.adjustment?.differenceBs || 0);
  const availableCreditBs = Math.max(0, Number(user?.credit_balance_bs || 0));
  // How much of the wallet we'd actually spend (capped by the diff).
  const creditToApply = useCredit ? Math.min(availableCreditBs, differenceBs) : 0;
  const remainingBs = Math.max(0, differenceBs - creditToApply);
  // When the wallet fully covers the diff, no transfer/proof is needed.
  const walletCoversAll = creditToApply + 0.5 >= differenceBs && differenceBs > 0;

  React.useEffect(() => {
    if (adjustPayOrder) {
      setRef("");
      setFile(null); setFileName("");
      setSelectedPmId(paymentMethods[0]?.id || "");
      setUseCredit(false);
    }
  }, [adjustPayOrder, paymentMethods]);

  // Keep the paid-amount input synced with the actual remainder to charge.
  React.useEffect(() => {
    if (!adjustPayOrder) return;
    setPaidAmount(remainingBs > 0 ? remainingBs.toFixed(2) : "");
  }, [adjustPayOrder, remainingBs]);

  const availablePms = useMemo(
    // Backend field is `active` (bool), NOT `isActive`. Legacy docs also
    // predate any `active` flag entirely; treat missing → active.
    () => paymentMethods?.filter((p) => p.active !== false),
    [paymentMethods]
  );

  if (!adjustPayOrder) return null;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFileName(f.name);
  };

  const submit = async (e) => {
    e.preventDefault();
    // When the wallet covers everything we skip the method + proof checks.
    if (!walletCoversAll) {
      if (!selectedPmId) { showToast("Falta método", "Selecciona un método de pago.", "error"); return; }
      if (!file) { showToast("Falta comprobante", "Adjunta la imagen del pago restante.", "error"); return; }
      const amt = parseFloat(paidAmount);
      if (!Number.isFinite(amt) || amt <= 0) { showToast("Monto inválido", "Ingresa un monto mayor a 0.", "error"); return; }
    }
    setBusy(true);
    try {
      await api.submitAdjustmentPayment(adjustPayOrder.id, {
        paymentMethodId: walletCoversAll ? "" : selectedPmId,
        paidAmountBs: walletCoversAll ? 0 : parseFloat(paidAmount) || 0,
        referenceNumber: ref.trim(),
        useCreditBs: creditToApply,
        proof: walletCoversAll ? null : file,
      });
      // Close first, then trigger a fresh /orders reload via ctx.loadOrders
      // (which is already the canonical way to refresh the UI list).
      setAdjustPayOrder(null);
      await loadOrders();
      showToast(
        walletCoversAll ? "Pedido Completado" : "Comprobante enviado",
        walletCoversAll
          ? "Se descontó de tu saldo a favor y tu pedido quedó listo."
          : "El admin verificará y completará tu pedido.",
        "success",
      );
    } catch (err) {
      showToast("Error", err.message || "No se pudo enviar", "error");
    } finally {
      setBusy(false);
    }
  };

  const adj = adjustPayOrder.adjustment || {};

  return (
    <Modal onClose={() => !busy && setAdjustPayOrder(null)} size="lg" testId="adjust-pay-modal">
      <form data-testid="adjustment-pay-modal-form-1" className="space-y-4" onSubmit={submit}>
        <div className={MODAL_HEADER}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-cyan-950/60 border border-cyan-500 flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Pagar Diferencia</h3>
              <p className="text-[10px] font-mono text-zinc-500">{adjustPayOrder.id} · {adj.newGameName || adjustPayOrder.gameName}</p>
            </div>
          </div>
          <button data-testid="adjustment-pay-modal-button-2" type="button" onClick={() => !busy && setAdjustPayOrder(null)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-700/60 space-y-1 text-xs">
          <p className="text-amber-200 font-semibold">Motivo del ajuste</p>
          <p className="text-amber-100/90 italic">&ldquo;{adj.note || ""}&rdquo;</p>
          <div className="flex items-center justify-between pt-2 border-t border-amber-800/40">
            <span className="text-amber-100">Diferencia a pagar:</span>
            <strong className="text-amber-200 text-base" data-testid="adjust-pay-amount">{formatBs(differenceBs)}</strong>
          </div>
        </div>

        {/* Wallet credit toggle — shown only when the user has a positive
            credit_balance_bs. Capped by the pending difference; when the
            wallet fully covers the delta the method-select + proof upload
            are auto-hidden and the submit button changes verbiage. */}
        {availableCreditBs > 0.5 && (
          <label
            data-testid="adjust-pay-use-credit-toggle"
            className={`flex items-start gap-3 p-3 rounded-xl border text-xs cursor-pointer transition-colors ${
              useCredit
                ? "bg-emerald-950/40 border-emerald-500/60"
                : "bg-zinc-900 border-zinc-700 hover:border-zinc-500"
            }`}
          >
            <input data-testid="adjustment-pay-modal-input-3"
              type="checkbox"
              checked={useCredit}
              onChange={(e) => setUseCredit(e.target.checked)}
              className="mt-0.5 accent-emerald-400"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-emerald-100 font-semibold">Usar mi saldo a favor</span>
                <span className="text-emerald-200 font-mono">Disponible: <strong>{formatBs(availableCreditBs)}</strong></span>
              </div>
              {useCredit && (
                <div className="mt-1.5 text-[10px] font-mono text-emerald-200/80">
                  Aplicando <strong>{formatBs(creditToApply)}</strong> · Falta por transferir <strong className="text-white">{formatBs(remainingBs)}</strong>
                </div>
              )}
            </div>
          </label>
        )}

        {!walletCoversAll && (
        <FormField label="Método de pago para la diferencia">
          <select
            data-testid="adjust-pay-method-select"
            value={selectedPmId}
            onChange={(e) => setSelectedPmId(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-cyan-500">
            <option value="">— Elegir método —</option>
            {availablePms?.map((pm) => {
              // Payment methods carry `bankName` + `type` (e.g. PAGO_MOVIL) —
              // the legacy `name` field doesn't exist, which is why the select
              // rendered blank rows. Build a human-friendly label the same way
              // the main checkout modal does.
              const typeLabel = pm.type === "PAGO_MOVIL" ? "Pago Móvil"
                : pm.type === "TRANSFERENCIA" ? "Transferencia"
                : pm.type === "USD_PLATFORM" ? (pm.platformName || "USD")
                : pm.type === "PLATAFORMA_USD" ? (pm.platformName || "USD")
                : pm.type || "";
              const label = [pm.bankName || pm.platformName, typeLabel].filter(Boolean).join(" · ");
              return <option key={pm.id} value={pm.id}>{label || pm.id}</option>;
            })}
          </select>
        </FormField>
        )}

        {!walletCoversAll && (
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Monto pagado (Bs)">
            <input
              type="number" step="0.01" min="0"
              data-testid="adjust-pay-amount-input"
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              placeholder={remainingBs.toFixed(2)}
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-cyan-500"
            />
          </FormField>
          <FormField label="Referencia (últimos 6 dígitos)">
            <input
              type="text"
              data-testid="adjust-pay-ref-input"
              value={ref}
              onChange={(e) => setRef(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))}
              placeholder="Opcional"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-xl p-2.5 text-sm text-white focus:outline-none focus:border-cyan-500"
            />
          </FormField>
        </div>
        )}

        {!walletCoversAll && (
        <FormField label="Comprobante (imagen)">
          <label
            data-testid="adjust-pay-file-picker"
            className="flex items-center gap-2 p-3 border border-dashed border-zinc-700 rounded-xl cursor-pointer hover:bg-zinc-900/40">
            <Upload className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-zinc-300">{fileName || "Sube la captura del pago"}</span>
            <input data-testid="adjustment-pay-modal-input-4" type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        </FormField>
        )}

        <div className={MODAL_ACTIONS}>
          <button data-testid="adjustment-pay-modal-button-5"
            type="button"
            onClick={() => setAdjustPayOrder(null)}
            disabled={busy}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || (!walletCoversAll && (!file || !selectedPmId))}
            data-testid="confirm-adjust-pay-btn"
            className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
            <span>
              {busy
                ? "Enviando..."
                : walletCoversAll
                  ? "Pagar con Saldo"
                  : "Enviar Comprobante"}
            </span>
          </button>
        </div>
      </form>
    </Modal>
  );
}
